import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const useUIStore = create(
  persist(
    (set, get) => ({
      // Sidebar
      sidebarOpen: true,
      sidebarCollapsed: false,

      // Theme
      themeMode: 'light',
      primaryColor: '#2ab63f',

      // Layout
      layoutMode: 'vertical',

      // Notifications
      notifications: [],
      unreadNotifications: 0,

      // Dialogs/Modals
      confirmDialog: {
        open: false,
        title: '',
        message: '',
        confirmText: '',
        cancelText: '',
        confirmColor: 'primary',
        severity: 'warning',
        onConfirm: null,
        onCancel: null,
      },

      // Snackbar/Toast
      snackbar: {
        open: false,
        message: '',
        severity: 'info',
        duration: 4000,
      },

      // Loading states
      globalLoading: false,
      loadingMessage: '',

      // Actions
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

      setSidebarOpen: (open) => set({ sidebarOpen: open }),

      toggleSidebarCollapsed: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

      setThemeMode: (mode) => set({ themeMode: mode }),

      toggleThemeMode: () => set((state) => ({
        themeMode: state.themeMode === 'light' ? 'dark' : 'light'
      })),

      setPrimaryColor: (color) => set({ primaryColor: color }),

      setLayoutMode: (mode) => set({ layoutMode: mode }),

      // Notifications
      setNotifications: (notifications) => set(() => ({
        notifications,
        unreadNotifications: notifications.filter((n) => !n.read).length,
      })),

      addNotification: (notification) => set((state) => ({
        notifications: [notification, ...state.notifications],
        unreadNotifications: state.unreadNotifications + 1
      })),

      markNotificationRead: (id) => set((state) => ({
        notifications: state.notifications.map(n =>
          n.id === id ? { ...n, read: true } : n
        ),
        unreadNotifications: Math.max(0, state.unreadNotifications - 1)
      })),

      markAllNotificationsRead: () => set((state) => ({
        notifications: state.notifications.map(n => ({ ...n, read: true })),
        unreadNotifications: 0
      })),

      clearNotifications: () => set({ notifications: [], unreadNotifications: 0 }),

      // Confirm Dialog
      openConfirmDialog: ({
        title,
        message,
        confirmText,
        cancelText,
        confirmColor,
        severity,
        onConfirm,
        onCancel,
      }) => set({
        confirmDialog: {
          open: true,
          title,
          message,
          confirmText,
          cancelText,
          confirmColor,
          severity,
          onConfirm,
          onCancel,
        }
      }),

      closeConfirmDialog: () => set({
        confirmDialog: {
          open: false,
          title: '',
          message: '',
          confirmText: '',
          cancelText: '',
          confirmColor: 'primary',
          severity: 'warning',
          onConfirm: null,
          onCancel: null,
        }
      }),

      // Snackbar
      showSnackbar: ({ message, severity = 'info', duration = 4000 }) => set({
        snackbar: { open: true, message, severity, duration }
      }),

      hideSnackbar: () => set((state) => ({
        snackbar: { ...state.snackbar, open: false }
      })),

      showSuccess: (message) => get().showSnackbar({ message, severity: 'success' }),

      showError: (message) => get().showSnackbar({ message, severity: 'error' }),

      showWarning: (message) => get().showSnackbar({ message, severity: 'warning' }),

      showInfo: (message) => get().showSnackbar({ message, severity: 'info' }),

      // Global Loading
      setGlobalLoading: (loading, message = '') => set({
        globalLoading: loading,
        loadingMessage: message
      }),
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        themeMode: state.themeMode,
        primaryColor: state.primaryColor,
        layoutMode: state.layoutMode,
      }),
    }
  )
);

export default useUIStore;
